import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, useTheme } from "@mui/material";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import __ from "src/utils/Translations";
import useIsAhr from "src/utils/hooks/useIsAhr";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  absencesAbsenceRequestsImport,
  absencesAbsenceRequestsRead,
  absencesAbsencesConfigurationRead,
  absencesAbsencesConfigurationWrite,
  absencesEmployeeAbsenceRecordsRead,
  absencesOtherEmployeeAbsenceRequestsCreate,
  reportAbsentsCli0051,
  reportAbsentsCli0052,
  reportAbsentsCli0053,
} from "src/utils/RoleBasedSecurity/permissions";
import { fetchEmployeeSearch } from "src/Pages/Company/Absences/api";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import EmployeesAutocompleteState from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteState";
import LinkBox from "src/Pages/Company/Absences/Main/LinkBox/LinkBox";
import LinkList from "src/Pages/Company/Absences/Main/LinkList/LinkList";
import CalendarPerson from "src/theme/Icons/CalendarPerson";
import CalendarListIcon from "src/theme/Icons/CalendarList";
import CalendarCatalogIcon from "src/theme/Icons/CalendarCatalog";
import { IEmployeeDetails } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

export default function AbsencesMain() {
  const theme = useTheme();
  const history = useHistory();
  const basePath = useBasePath();
  const isAhr = useIsAhr();
  const companyId = useCompanyId();
  const canCreateAbsenceRequestForEmployee = usePermission(
    absencesOtherEmployeeAbsenceRequestsCreate
  );
  const canReadAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsRead
  );
  const canReadAbsenceRequests = usePermission(absencesAbsenceRequestsRead);
  const canReadAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationRead
  );
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const moduleSettingsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ABSENCES_MODULE_SETTINGS
  );
  const canReadAvailableBalancesReports = usePermission(reportAbsentsCli0051);
  const canReadUsedBalancesReports = usePermission(reportAbsentsCli0052);
  const canReadAbsenceRequestsReports = usePermission(reportAbsentsCli0053);
  const canReadAnyReport = isAhr
    ? canReadAvailableBalancesReports ||
      canReadUsedBalancesReports ||
      canReadAbsenceRequestsReports
    : true;

  const [selectedEmployee, setSelectedEmployee] =
    useState<IEmployeeDetails | null>(null);

  const handleEmployeeChange = (newValue: IEmployeeDetails | null) => {
    setSelectedEmployee(newValue);

    if (!newValue) return;

    history.push(`${basePath}/absences/employee/${newValue?.employeeId}`);
  };

  const linkLists = [
    {
      title: __("Konfiguracja nieobecności:"),
      links: [
        {
          label: __("Grupy nieobecności i obecności"),
          url: "absences/absence-groups",
          disabled: !canReadAbsencesConfiguration,
        },
        {
          label: __("Szablony wniosków"),
          url: "absences/absence-types",
          disabled: !canReadAbsencesConfiguration,
        },
        {
          label: __("Tworzenie nowego szablonu wniosku"),
          url: "absences/absence-type",
          disabled: !canWriteAbsencesConfiguration,
        },
        {
          label: __("Lista kalendarzy"),
          url: "absences/calendars",
          disabled: !canReadAbsencesConfiguration,
        },
        // Module settings only for OMB
        ...(isAhr || !moduleSettingsEnabled
          ? []
          : [
              {
                label: __("Ustawienia modułu nieobecności"),
                url: "absences/module-settings",
                disabled: !absencesAbsencesConfigurationWrite,
              },
            ]),
        {
          label: __("Import wniosków z innego systemu"),
          url: "absences/import-absence-requests",
          disabled: !absencesAbsenceRequestsImport,
        },
      ],
    },
  ];

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
          },
        ]}
      />
      <Title
        title={__("Panel nieobecności")}
        subtitle={__("Panel administracyjny")}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: "2rem",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2rem",
            alignItems: "start",
          }}
        >
          <LinkBox
            title={__("Kartoteki nieobecności Pracowników")}
            description={__(
              "Wyszukaj pracownika, aby zarządzać akcjami na jego koncie."
            )}
            icon={
              <CalendarCatalogIcon
                fontSize="small"
                fill={
                  canReadAbsenceRecords
                    ? theme.palette.primary.main
                    : theme.palette.text.disabled
                }
              />
            }
            link={{
              label: __("Przejdź do Kartoteki nieobecności Pracowników"),
              url: "absences/absence-records",
            }}
            disabled={!canReadAbsenceRecords}
            sx={{
              gridColumn: "1 / -1",
              minHeight: "unset",
            }}
          >
            <Box mt="2rem">
              <EmployeesAutocompleteState
                name="employeeSelect"
                label={__("Szukaj pracowników")}
                queryKey="employee-search"
                queryFn={fetchEmployeeSearch}
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                enabled
              />
            </Box>
          </LinkBox>
          <LinkBox
            title={__("Złóż wniosek za pracownika")}
            description={__("W tej przestrzeni:")}
            list={[__("złożysz wniosek w imieniu pracownika.")]}
            link={{
              label: __("Przejdź do wniosku"),
              url: "absences/absence-request/create",
            }}
            icon={
              <CalendarPerson
                fontSize="small"
                fill={
                  canCreateAbsenceRequestForEmployee
                    ? theme.palette.primary.main
                    : theme.palette.text.disabled
                }
              />
            }
            disabled={!canCreateAbsenceRequestForEmployee}
            buttonLink
          />
          <LinkBox
            title={__("Lista złożonych wniosków")}
            description={__("W tej przestrzeni możesz:")}
            list={[
              __("zarządzać wnioskami nieobecności i obecności w organizacji."),
            ]}
            link={{
              label: __("Przejdź do wniosków"),
              url: "absences/absence-requests",
            }}
            icon={
              <CalendarListIcon
                fontSize="small"
                fill={
                  canReadAbsenceRequests
                    ? theme.palette.primary.main
                    : theme.palette.text.disabled
                }
              />
            }
            disabled={!canReadAbsenceRequests}
            buttonLink
          />
          <LinkBox
            title={__("Nieobecności w Module Raportów")}
            description={__("W tej przestrzeni wygenerujesz:")}
            list={[
              __("raport dostępnych wymiarów nieobecności,"),
              __("raport wykorzystanych wymiarów nieobecności,"),
              __("listę złożonych wniosków."),
            ]}
            link={{
              label: __("Przejdź do Raportów"),
              url: isAhr
                ? `${basePath}/report/predefined-reports${
                    companyId ? `?companyId=${companyId}` : ""
                  }`
                : `/report/predefined-reports${
                    companyId ? `?companyId=${companyId}` : ""
                  }`,
            }}
            icon={
              <CalendarListIcon
                fontSize="small"
                fill={
                  canReadAnyReport
                    ? theme.palette.primary.main
                    : theme.palette.text.disabled
                }
              />
            }
            disabled={!canReadAnyReport}
            buttonLink
          />
        </Box>
        <Box>
          {linkLists.map((list) => (
            <LinkList key={list.title} list={list} />
          ))}
        </Box>
      </Box>
    </>
  );
}
